import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Mail, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent — we'll be in touch soon.");
    } catch {
      toast.error("Couldn't send. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <Navbar />
      <section className="pt-12 md:pt-16 pb-10 bg-[#FFC2E8] border-b-2 border-[#0A0A0A]">
        <div className="container-main">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#0A0A0A] hover:underline mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <p className="overline text-[#0A0A0A]/70 mb-3">Get in touch</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-[#0A0A0A] mb-3">Contact us</h1>
          <p className="text-base md:text-lg text-[#0A0A0A]/80 max-w-2xl">
            Questions, partnership ideas, press inquiries, or just want to say hello? Drop us a line.
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            {sent ? (
              <div className="bg-[#0A0A0A] text-white border-2 border-[#0A0A0A] p-10 text-center shadow-[6px_6px_0px_0px_rgba(255,194,232,1)]" data-testid="contact-success">
                <h2 className="text-3xl font-display font-bold mb-3 text-[#FF2D87]">Thank you</h2>
                <p className="text-white/80 mb-6">Your message is in. Rachel and the team will reply soon.</p>
                <button onClick={() => setSent(false)} className="btn-primary" data-testid="contact-send-another">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/70 mb-2 block">Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] transition-colors"
                      placeholder="Your name"
                      data-testid="contact-name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/70 mb-2 block">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] transition-colors"
                      placeholder="you@example.com"
                      data-testid="contact-email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/70 mb-2 block">Subject</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] transition-colors"
                    placeholder="Partnership, press, membership question…"
                    data-testid="contact-subject"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/70 mb-2 block">Message</label>
                  <textarea
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full px-4 py-3 bg-white border-2 border-[#0A0A0A] focus:outline-none focus:border-[#FF2D87] transition-colors resize-none"
                    placeholder="Tell us what's on your mind…"
                    data-testid="contact-message"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {sending ? "Sending…" : (
                    <>
                      <Send className="w-4 h-4" />
                      Send message
                    </>
                  )}
                </button>

                <p className="text-xs text-[#4A4A4A] pt-2 inline-flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" />
                  Or email us directly at{" "}
                  <a href="mailto:hello@thecreativeensemble.com" className="text-[#FF2D87] hover:underline font-bold">
                    hello@thecreativeensemble.com
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
